import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios'; // ✅ Import axios for API calls
import { UserContext } from '../../UserContext'; // ✅ Import User Context

const BalancedDiet = () => {
  const { user } = useContext(UserContext); // ✅ Get user from context
  const category = 'balanceddiet'; // ✅ Category for fetching videos
  const [videos, setVideos] = useState([]); // ✅ State to hold videos
  const [loading, setLoading] = useState(true); // ✅ Loading state

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`https://wholeness-forward.onrender.com/api/video/videos/${category}`);
        if (Array.isArray(response.data)) {
          setVideos(response.data);
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // ✅ Handle Mark as Helpful for logged-in users only
  const handleMarkHelpful = async (videoId) => {
    if (!user || !user.id) {
      alert('You must be logged in to mark as helpful.');
      return;
    }

    try {
      const response = await axios.post(`https://wholeness-forward.onrender.com/api/video/videos/toggleHelpful`, {
        videoId,
        userId: user.id,
      });

      // ✅ Update UI with new helpful count
      setVideos((prevVideos) =>
        prevVideos.map((video) =>
          video._id === videoId
            ? { ...video, helpfulCount: response.data.helpfulCount }
            : video
        )
      );
    } catch (error) {
      console.error('Error marking video as helpful:', error);
    }
  };

  return (
    <div className="balanced-diet-page page-container">
      <h1 className="page-title">Building a Balanced Diet</h1>
      <p className="page-description">
        Welcome to the Balanced Diet section of Wholeness Forward.
      </p>

      <img
        src="/images/wf-balanceddiet.webp"
        alt="Balanced Diet Illustration"
        className="page-image"
        width="330"
      />

      <p className="page-description">
        A balanced diet is essential for maintaining overall health and wellness. It provides the body with all the necessary nutrients in the right proportions to function optimally.
      </p>

      <h2 className="section-title">Key Components of a Balanced Diet</h2>
      <ul className="content-list">
        <li><strong>Carbohydrates:</strong> The body's primary energy source, found in grains, fruits, and vegetables.</li>
        <li><strong>Proteins:</strong> Essential for muscle growth, found in meat, fish, legumes, and dairy.</li>
        <li><strong>Fats:</strong> Healthy fats, such as omega-3s, support brain function and hormone production.</li>
        <li><strong>Vitamins and Minerals:</strong> Crucial for immune function, energy, and bone health.</li>
        <li><strong>Water:</strong> Vital for hydration and digestion. Aim for 8 cups (64 ounces) per day.</li>
      </ul>

      <h3 className="section-title">How to Create a Balanced Meal</h3>
      <p className="section-description">
        A balanced meal includes carbohydrates, proteins, and fats, along with plenty of fruits and vegetables.
      </p>
      <ul className="content-list">
        <li><strong>Half the plate:</strong> Non-starchy vegetables or fruits.</li>
        <li><strong>One-quarter:</strong> Lean protein (e.g., chicken, tofu, beans).</li>
        <li><strong>One-quarter:</strong> Whole grains (e.g., brown rice, quinoa).</li>
        <li><strong>Include healthy fats:</strong> Like olive oil, avocado, or nuts.</li>
      </ul>

      <hr className="section-divider" />
      
      <h2 className="section-title">Curated Video Collection</h2>

      {loading ? (
        <p className="loading-message">Loading videos...</p>
      ) : (
        <div className="video-container">
          {videos.length > 0 ? (
            videos.map((video) => (
              <div key={video._id} className="video-card">
                <h3 className="video-title">{video.title}</h3>
                <p className="video-details">
                  <strong>Source:</strong> {video.source} <br />
                  <strong>Length:</strong> {video.length} <br />
                  {video.description}
                </p>
                <iframe
                  className="video-frame"
                  src={video.url}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>

                {/* ✅ Helpful Button */}
                <button
                  className="button"
                  onClick={() => handleMarkHelpful(video._id)}
                  disabled={video.usersWhoClickedHelpful?.includes(user?.id)}
                >
                  {video.usersWhoClickedHelpful?.includes(user?.id)
                    ? `Helpful (${video.helpfulCount})`
                    : `Mark as Helpful (${video.helpfulCount || 0})`}
                </button>
              </div>
            ))
          ) : (
            <p className="no-videos-message">No videos found for this category.</p>
          )}
        </div>
      )}


      <hr className="section-divider" />

      {/* Forum Section */}
      <div className="forum-section">
        <h2 className="section-title">Join the Balanced Diet Discussion</h2>
        <p className="section-description">
          Have questions or insights about a balanced diet? Join our forum to connect with others who are exploring this topic.
        </p>
        <a href="/forum/Balanced_Diet" className="forum-link">
          Go to Balanced Diet Forum
        </a>
      </div>
    </div>
  );
};

export default BalancedDiet;
